<template>
    <div>
        <SToolbar>
            <v-icon small class="mr-1">ic-20 ic-agenda ic-col-grey</v-icon>
            <v-toolbar-title>{{ $t('agenda') }}</v-toolbar-title>
        </SToolbar>
        <v-skeleton-loader v-if="!record" type="article, actions" />
        <v-card v-else height="var(--usable-height)" class="main-grid">
            <LoadingOverlay :value="isSaving" absolute />

            <!-- Header with Buttons -->
            <div class="top-header">
                <light-toolbar dense>
                    <v-toolbar-title>
                        <span>{{ headerFirstPart }}</span>
                        <span v-if="headerSecondPart" class="record-title">{{ headerSecondPart }}</span>
                        <span v-if="userIsStaff() && record.event_organizer" class="tgk-paragraph-text">
                            ({{ record.event_organizer }})
                        </span>
                        <EventModeIndicator :event="record" class="ml-2" />
                    </v-toolbar-title>

                    <v-spacer />
                    <span v-if="record?.bp_import_origin" class="mr-5">
                        <KpIcon icon-cls="eventfrog" color="grey" left />
                        <span class="grey--text"
                            >{{ $t('quelle') }}:
                            {{
                                lookupDataProvider(record?.bp_import_origin)?.provider || record?.bp_import_origin
                            }}</span
                        >
                    </span>
                    <span v-if="record?.import_protection" class="mr-5">
                        <KpIcon icon-cls="importsafe" color="grey" left />
                        <span class="grey--text">{{ $t('import_protection') }}</span>
                    </span>
                    <span>
                        <KpIcon :icon-cls="`${record?.status_icon}`" color="grey" left />
                        <span class="grey--text">{{ $t(record?.status) }}</span>
                    </span>
                </light-toolbar>
                <KpAlert
                    v-if="!recordSaveable()"
                    class="ma-2"
                    dismissible
                    type="warning"
                    icon="ic ic-24 ic-alert ic-col-white"
                >
                    {{ $t('info_event_not_saved') }}
                </KpAlert>
                <v-toolbar dense class="elevation-0" color="primary white--text">
                    <v-btn
                        v-if="!hidePreview"
                        color="action white--text"
                        depressed
                        :disabled="record?.isPhantom()"
                        :href="getUrl(true)"
                        target="tgk_preview"
                    >
                        <KpIcon icon-cls="preview" color="white" disabled-color="grey" left />
                        {{ $t('preview') }}
                    </v-btn>

                    <v-spacer v-if="!hidePreview" />

                    <v-btn
                        v-if="userIsStaff()"
                        :color="record?.to_newsletter ? 'primaryToggled' : 'primary'"
                        depressed
                        @click="onInput('to_newsletter', !record?.to_newsletter, true)"
                    >
                        <KpIcon icon-cls="tonewsletter" color="white" left />
                        {{ $t('fuer_nl') }}
                    </v-btn>
                    <v-btn
                        v-if="userIsStaff()"
                        class="ml-1"
                        :color="record?.is_featured ? 'primaryToggled' : 'primary'"
                        depressed
                        @click="onInput('is_featured', !record?.is_featured, true)"
                    >
                        <KpIcon icon-cls="typed" color="white" left />
                        {{ $t('tippen') }}
                    </v-btn>
                    <v-btn
                        v-if="userIsStaff()"
                        class="ml-1"
                        :color="record?.import_protection ? 'primaryToggled' : 'primary'"
                        depressed
                        @click="onInput('import_protection', !record?.import_protection, true)"
                    >
                        <KpIcon icon-cls="importsafe" color="white" left />
                        {{ $t('import_protection') }}
                    </v-btn>
                    <v-btn
                        v-if="userIsStaff()"
                        :color="record?.edited ? 'primaryToggled' : 'primary'"
                        depressed
                        @click="onInput('edited', !record?.edited, true)"
                    >
                        <KpIcon icon-cls="published" color="white" />
                        {{ $t('redigiert') }}
                    </v-btn>
                </v-toolbar>
            </div>

            <!-- Navigation -->
            <div class="navigation">
                <v-tabs vertical background-color="transparent" hide-slider>
                    <v-tab
                        v-for="navItem in navItems"
                        :key="navItem.id"
                        :disabled="!Number(record?.getId())"
                        class="nav-tab"
                        :to="{ name: navItem.routeName }"
                        exact
                    >
                        <KpIcon :icon-cls="`${navItem.icon}`" color="grey" active-color="black" left />
                        {{ navItem.titleFn ? navItem.titleFn() : navItem.title }}
                    </v-tab>
                </v-tabs>
            </div>

            <!-- Main content -->
            <div class="content">
                <router-view
                    ref="activeChild"
                    :record="record"
                    @input="isValid = $event"
                    @saving="isSaving = $event"
                    @featured-image-created="onFeaturedImagesCreated"
                    @save="save"
                    @reload-event="reload"
                />
            </div>

            <!-- footer -->
            <div class="bottom-toolbar d-flex justify-space-between">
                <div class="d-flex justify-end">
                    <PlainBtn class="ml-3" @click="cancel()">
                        {{
                            backRoute?.displayText ? $t('back_to', { text: backRoute?.displayText }) : $t('schliessen')
                        }}
                    </PlainBtn>
                    <PlainBtn v-if="!firstItem" @click="goBack">{{ $t('back') }}</PlainBtn>
                    <ActionBtn v-if="showSaveBtn" :disabled="!isValid" :loading="isSaving" class="ml-3" @click="save">
                        <KpIcon icon-cls="save" color="white" left />
                        {{ $t('save') }}
                    </ActionBtn>
                    <ActionBtn
                        v-if="showNextBtn"
                        class="ml-3"
                        :disabled="!isValid"
                        :loading="isSaving"
                        @click="saveAndNext"
                    >
                        <KpIcon v-if="showSaveBtn" icon-cls="save" color="white" left />{{
                            showSaveBtn ? $t('save_next') : $t('next')
                        }}
                    </ActionBtn>
                </div>
            </div>
        </v-card>
    </div>
</template>

<script>
import { Event, EventCollection } from '@/models/Event';
import { errToStr, setBrowserTitle, shortenString } from '@/helpers/tools';
import { mapActions, mapState } from 'vuex';
import { globalParam } from '@/helpers/globals';
import { userIsStaff } from '@/helpers/auth';
import BackRouteMixin from '@/mixins/backroute';
import EventModeIndicator from '@/components/event/EventModeIndicator.vue';
import PlainBtn from '@/components/buttons/PlainBtn.vue';
import { lookupDataProvider } from '@/helpers/data_provider_composable';
import { commitToast } from '@/plugins/toast';

export default {
    name: 'EventEdit',
    components: { EventModeIndicator, PlainBtn },
    mixins: [BackRouteMixin],

    async beforeRouteUpdate(to, from, next) {
        await this.save();
        if (to.params.id !== from.params.id) {
            this.record = null;
        }
        this.loadRecord(to.params.id);
        next();
    },

    data() {
        return {
            isSaving: false,
            record: null,
            isValid: false,
            activeItemId: null,
        };
    },
    computed: {
        ...mapState('user', {
            user: 'record',
        }),
        firstItem() {
            const routeName = this.$route.name;
            return this.navItems[0].routeName === routeName;
        },
        lastItem() {
            const routeName = this.$route.name;
            return this.navItems[this.navItems.length - 1].routeName === routeName;
        },
        showSaveBtn() {
            return this.activeNavItem?.showSaveBtn === true;
        },
        showNextBtn() {
            return this.activeNavItem?.showNextBtn === true;
        },
        headerFirstPart() {
            const eventClass = this.record?.event_class || 'event';
            return `${this.$t('veranstaltungen_erfassen', { event_class: this.$t(eventClass) })}${
                this.headerSecondPart ? `: ` : ''
            }`;
        },
        headerSecondPart() {
            return this.record?.title;
        },
        browserTitle() {
            const parts = [];
            if (this.record?.title) {
                parts.push(shortenString(this.record?.title, 30), this.activeNavItem?.title);
            } else {
                const eventClass = this.record?.event_class || 'event';
                const createEventstring = this.$t('veranstaltungen_erfassen', { event_class: this.$t(eventClass) });
                parts.push(createEventstring);
            }
            parts.push(this.$t('event'));
            return parts.filter(Boolean).join(' - ');
        },
        activeNavItem() {
            const actRouteName = this.$route.name;
            const items = this.navItems.filter((navItem) => navItem.routeName === actRouteName);
            if (items?.length > 0) {
                return items[0];
            }
            return null;
        },
        isReviewByStaff() {
            return globalParam('event_review_by_staff');
        },
        showPublishView() {
            return userIsStaff() || !this.isReviewByStaff;
        },
        getAkGageView() {
            if (this.user.ak_module_active) {
                return {
                    id: 'event-gage',
                    routeName: 'event-gage',
                    icon: 'price',
                    title: this.$t('event_gage'),
                    showNextBtn: false,
                    showSaveBtn: true,
                };
            }
            return null;
        },
        getNavItemPanel() {
            if (!this.showPublishView) {
                return {
                    id: 'submit',
                    routeName: 'event-submit',
                    icon: 'published',
                    title: this.$t('submit'),
                    showNextBtn: false,
                    showSaveBtn: false,
                };
            }

            if (this.showPublishView) {
                return {
                    id: 'publish',
                    routeName: 'event-publish',
                    icon: 'published',
                    title: this.$t('publish'),
                    showNextBtn: false,
                    showSaveBtn: false,
                };
            }

            return null;
        },
        navItems() {
            return [
                {
                    id: 'event-class',
                    routeName: 'event-edit',
                    icon: 'agenda',
                    title: this.$t('veranstaltungsart'),
                    showNextBtn: true,
                    showSaveBtn: true,
                },
                {
                    id: 'categories',
                    routeName: 'event-info-categories',
                    icon: 'tiles',
                    title: this.$t('info_categories'),
                    showNextBtn: true,
                    showSaveBtn: true,
                },
                {
                    id: 'description',
                    routeName: 'event-description',
                    icon: 'alignleft',
                    title: this.$t('description'),
                    showNextBtn: true,
                    showSaveBtn: true,
                },
                {
                    id: 'info',
                    routeName: 'event-additional-info',
                    icon: 'info',
                    title: this.$t('additional_info'),
                    showNextBtn: true,
                    showSaveBtn: true,
                },
                // schedules: not all event classes have schedules:
                Event.CLASSES_WITH_SCHEDULES.indexOf(this.record?.event_class) >= 0
                    ? {
                          id: 'schedule',
                          routeName: 'event-schedule',
                          icon: 'calendar',
                          title: this.$t('schedule'),
                          showNextBtn: true,
                          showSaveBtn: true,
                      }
                    : null,
                {
                    id: 'images',
                    routeName: 'event-edit-image',
                    icon: 'mediamanager',
                    title: this.$t('titelbild'),
                    showNextBtn: true,
                    showSaveBtn: true,
                },
                // sub-events: not all event classes have sub-events:
                Event.CLASSES_WITH_SUB_EVENTS().indexOf(this.record?.event_class) >= 0
                    ? {
                          id: 'sub-events',
                          routeName: 'event-sub-events',
                          icon: 'subevent',
                          title: this.$t('sub_events'),
                          showNextBtn: true,
                          showSaveBtn: false,
                      }
                    : null,
                this.getNavItemPanel,
                {
                    id: 'metadata',
                    routeName: 'event-metadata',
                    icon: 'metainfos',
                    title: this.$t('metadata'),
                    showNextBtn: true,
                    showSaveBtn: false,
                },
                this.getAkGageView,
            ].filter(Boolean);
        },
        hidePreview() {
            return globalParam('event.hide_preview');
        },
    },
    watch: {
        browserTitle: {
            handler() {
                setBrowserTitle(this.getBrowserTitle());
            },
            immediate: true,
        },
    },
    async beforeDestroy() {
        if (!this.record?.isPhantom()) {
            await this.save();
        }
    },
    mounted() {
        this.loadRecord(this.$route.params.id);
    },
    methods: {
        ...mapActions(['runAsync']),
        userIsStaff,
        lookupDataProvider,
        async loadRecord(id) {
            // create a new record if requested, or load an existing one
            if (id === 'new' || !Number(id)) {
                this.record = new Event({
                    organizer_id: this.user.event_organizer_id,
                    location_id: this.user.event_organizer?.default_location_id,
                    event_organizer: this.user.event_organizer?.title,
                });
                // premature return in case of new record
                return;
            }
            // load existing record
            try {
                const collection = new EventCollection();
                await this.runAsync(() => collection.query({ id: id, includeOlder: true }));
                const record = collection.first();
                if (record) {
                    this.record = record;
                } else {
                    this.record = null;
                }
            } catch (e) {
                this.record = null;
                commitToast({ message: errToStr(e), color: 'error' });
            }
        },
        async reload() {
            this.loadRecord(this.record?.getId());
        },
        recordSaveable() {
            return this.record?.userCanSave(this.user);
        },
        async save() {
            if (!this.record) {
                return false;
            }
            // Access check:
            if (!this.record.userCanSave(this.user)) {
                return false;
            }
            try {
                this.isSaving = true;
                // give the child a change to do things before we save
                // e.g. save block contents
                if (this.$refs.activeChild.beforeSave) {
                    if (!(await this.$refs.activeChild.beforeSave())) {
                        return false;
                    }
                }
                const isNew = this.record?.isPhantom();
                if (this.record?.hasUnsavedData()) {
                    await this.runAsync(async () => await this.record?.save());
                }
                if (isNew) {
                    // when a new rec is created, replace the route...
                    this.$router.replace({ name: 'event-edit', params: { id: this.record?.getId() } });
                }
                return true;
            } catch (e) {
                commitToast({ message: errToStr(e), color: 'error' });
            } finally {
                this.isSaving = false;
            }
        },
        async saveAndNext() {
            if (this.showSaveBtn) {
                await this.save();
            }
            this.traverseNavItemBy(1);
        },
        traverseNavItemBy(number = 1) {
            const actItemEntry = this.activeNavItem;
            if (actItemEntry) {
                const nextIndex = this.navItems.indexOf(actItemEntry) + number;
                if (nextIndex >= 0 && this.navItems.length > nextIndex) {
                    this.$router.push({ name: this.navItems[nextIndex].routeName });
                }
            }
        },
        cancel() {
            if (this.getBackRouteConfig()?.route) {
                this.$router.push(this.getBackRouteConfig().route);
            } else {
                this.$router.push({ name: 'event' });
            }
        },
        async goBack() {
            await this.save();
            this.traverseNavItemBy(-1);
        },
        onInput(field, value, autoSave = false) {
            this.record?.set(field, value);
            if (autoSave) {
                this.save();
            }
        },
        getUrl(preview = false) {
            if (preview) {
                return this.record?.detailPreviewUrl;
            } else {
                return this.record?.detailUrl;
            }
        },
        onFeaturedImagesCreated() {
            this.save();
        },
        getBrowserTitle() {
            return this.browserTitle;
        },
    },
};
</script>

<style lang="scss" scoped>
.main-grid {
    display: grid;
    grid-template-rows: auto 1fr auto;
    grid-template-columns: 220px 1fr;
    grid-template-areas:
        'header header'
        'nav content'
        'nav bottom';
}

.top-header {
    grid-area: header;
}

.navigation {
    grid-area: nav;
    background-color: var(--toolbar-bg-color);
    padding: 12px;
    border-right: 1px solid var(--gray-border-color);
}

.content {
    grid-area: content;
    display: grid;
    grid-template-rows: minmax(0, 1fr);
    overflow: hidden;
}

.bottom-toolbar {
    grid-area: bottom;
    background-color: var(--toolbar-bg-color);
    padding: 10px 27px;
}

.nav-tab {
    height: initial !important;
    justify-content: start !important;
    padding: 5px 3px;
    margin: 3px;
    text-transform: none !important;
    font-weight: normal;
    font-size: 1rem;
    letter-spacing: 0;

    &.v-tab--active {
        color: var(--nearblack-color) !important;
    }
}

.record-title {
    color: initial;
}
</style>
